<template>
  <!-- 首頁（2026-10-04 首頁進化，報告 → Obsidian「31 - 2026-10-04 首頁完整進化：角色、內容、版面與動態」）。
       只有首頁能做的四件事排在前面：他是誰（一句話）、現在（近況）、作品（精選）、全站入口（實驗室、地層、音樂、思想、關於）。
       版型跟關於、實驗室同一套：--editorial-w、eyebrow「01 · NOW」、Noto Serif TC 大標、無框膠囊、章節之間一條 --line。
       品牌語言：開場那條基因條入座後，頁首主視覺下方接一條同一件作品的基因條；近況與精選每張卡都帶自己的基因條（用卡上那張圖，不多下載）。 -->
  <div class="page page-index home" :lang="isZh ? 'zh-Hant' : 'en'">
    <header class="home-top">
      <div class="home-top-name">
        <p class="home-eyebrow">{{ t.eyebrow }}</p>
        <h1 class="hero-name">
          <span class="name-latin">Che-Yu Wu</span>
          <span class="name-chinese" lang="zh-Hant">吳哲宇</span>
        </h1>
        <p class="home-motto">{{ $t('index.subtitle') }}</p>
      </div>
      <div class="home-top-id">
        <p class="home-identity">{{ identity }}</p>
        <p class="home-actions">
          <router-link class="home-pill is-primary" :to="localizedPath('/work')">{{ t.toWork }} →</router-link>
          <router-link class="home-pill" :to="localizedPath('/about')">{{ $t('nav.about') }}</router-link>
          <router-link class="home-pill" :to="localizedPath('/contact')">{{ $t('nav.contact') }}</router-link>
        </p>
      </div>
    </header>

    <!-- 主視覺跟近況連動（2026-10-04 哲宇：都照建議）：近況裡第一個還沒結束、連到有圖作品的項目；hero: true 優先；全部過期退回精選第一件。
         建置時與瀏覽器都用當天的台北日期判斷（home-now.mjs pickHero），過期自動換。有專用大圖的作品寫在 home-featured.json 的 hero.images。 -->
    <figure class="home-hero gene-host">
      <div class="home-hero-frame">
        <img
          ref="heroImg" class="home-hero-image" @load="onHeroLoad"
          fetchpriority="high" decoding="async" :width="hero.width" :height="hero.height" :sizes="hero.srcset ? '100vw' : null" :srcset="hero.srcset || null"
          :src="hero.src" :alt="hero.alt" :style="{ '--pos': hero.position || 'center', '--pos-m': hero.positionMobile || hero.position || 'center' }"
        />
        <button v-if="hero.film" ref="filmTrigger" type="button" class="home-film" @click="openFilm">
          <BrandIcon name="run" />{{ isZh ? '觀看排練紀錄' : 'Watch rehearsal film' }}
        </button>
      </div>
      <figcaption class="home-hero-cap">
        <router-link class="home-hero-work" :to="localizedPath(hero.to)">
          <span class="home-eyebrow">{{ hero.eyebrow }}<em v-if="hero.status" class="home-now-status" :class="'is-' + hero.status">{{ t.status[hero.status] }}</em></span>
          <h2>{{ hero.title }}</h2>
          <!-- 基因條在作品名下方（2026-10-04 19:00 哲宇：貼著照片「看起來不像基因，比較像是壞掉」）：跟 /work 卡片同一個語言，條屬於文字，不是照片的邊框。
               條碼用主視覺「實際載入的那張」（srcset 挑中的 currentSrc），手機也有、不多下載；高度先留好（CLS 0），預渲染不放。 -->
          <span class="home-hero-gene"><HomeGene v-if="heroShown" :key="heroShown" :slug="hero.slug" :src="heroShown" /></span>
          <span class="home-link">{{ isZh ? '進入作品' : 'Explore the work' }} →</span>
        </router-link>
        <div class="home-hero-context">
          <p>{{ hero.context }}</p>
          <small v-if="hero.credit">{{ hero.credit }}</small>
        </div>
      </figcaption>
    </figure>

    <!-- 01 近況：現在去得到、看得到的。每一項連到它自己的作品或展覽頁（資料在 i18n about.nowItems[].to，跟 About 共用） -->
    <section v-if="nowItems.length" ref="nowEl" class="home-sec home-now" aria-labelledby="home-now-title">
      <div class="home-head">
        <p class="home-eyebrow">01 · NOW</p>
        <h2 id="home-now-title">{{ t.nowTitle }}</h2>
      </div>
      <div class="home-now-list">
        <router-link v-for="item in nowItems" :key="item.label" :to="localizedPath(item.to || '/about')" class="home-now-item gene-host">
          <span class="home-now-pic"><img v-if="item.cover" loading="lazy" decoding="async" width="1200" height="800" :src="near.now ? item.cover : null" alt="" /></span>
          <span class="home-now-when">
            <time>{{ item.date }}</time>
            <em class="home-now-status" :class="item.status ? 'is-' + item.status : ''">{{ item.status ? t.status[item.status] : '' }}</em>
          </span>
          <span class="home-now-label">{{ item.label }}</span>
          <span class="home-now-place">{{ item.place }}</span>
          <!-- 基因條在文字的最下面、同一列卡片對齊（不貼圖，19:00 哲宇） -->
          <span class="home-now-gene"><HomeGene v-if="item.slug && item.cover" :slug="item.slug" :src="item.cover" /></span>
        </router-link>
      </div>
    </section>

    <!-- 02 精選作品：順序住在 src/data/home-featured.json；前兩件大、其餘六件小，每件帶自己的基因條 -->
    <section ref="worksEl" class="home-sec home-works" aria-labelledby="home-works-title">
      <div class="home-head home-head-row">
        <div>
          <p class="home-eyebrow">02 · SELECTED WORKS</p>
          <h2 id="home-works-title">{{ isZh ? '精選作品' : 'Selected works' }}</h2>
        </div>
        <p class="home-head-links">
          <router-link class="home-link" :to="localizedPath('/work')">{{ t.toWork }} →</router-link>
          <router-link class="home-link" :to="localizedPath('/work/archive')">{{ t.archive }} →</router-link>
        </p>
      </div>
      <ul class="home-works-grid">
        <li v-for="(work, i) in featuredWorks" :key="work.slug" :class="{ 'is-large': i < 2 }">
          <router-link class="home-work gene-host" :to="localizedPath('/work/' + work.slug)">
            <span class="selected-image"><img loading="lazy" decoding="async" width="1200" height="800" :src="near.works ? work.cover : null" :alt="workTitle(work)" /></span>
            <span class="home-eyebrow">{{ work.year }} · {{ categoryLabel(work.category) }}</span>
            <h3>{{ workTitle(work) }}</h3>
            <HomeGene :slug="work.slug" :src="work.cover" class="home-work-gene" />
            <p v-if="i < 2">{{ isZh ? work.descriptionZh || work.description : work.description }}</p>
          </router-link>
        </li>
      </ul>
    </section>

    <!-- 03 · 2025 年度回顧（2026-10-04 哲宇：「不要只有一個影片這樣放在這」，19:05 定結構：演出、展出、教學、合作）。
         清單住在 src/data/home-2025.json（出處在 docs/data/home-2025-sources.json，不進網頁；導言的件數由清單自動填）。影片只放海報、捲到附近才給圖、按了才載 YouTube（17:40「我還是想放首頁耶」）。 -->
    <section ref="filmEl" class="home-sec home-year" aria-labelledby="home-year-title">
      <div class="home-head">
        <p class="home-eyebrow">03 · 2025</p>
        <h2 id="home-year-title">{{ isZh ? '2025 年度回顧' : '2025 in review' }}</h2>
      </div>
      <div class="home-year-top">
        <p class="home-year-intro">{{ yearIntro }}</p>
        <div class="home-film-box">
          <ClickFilm video-id="E2qRrNNSSQM" title="MONOLAB 2025 Annual Highlights" credit="Monolab Official" :poster="near.film ? 'https://i.ytimg.com/vi/E2qRrNNSSQM/maxresdefault.jpg' : null" source="https://www.youtube.com/watch?v=E2qRrNNSSQM" :zh="isZh" />
          <blockquote class="home-year-quote">
            <p>{{ isZh ? '「' : '“' }}{{ year2025.film.quote[L] }}{{ isZh ? '」' : '”' }}</p>
            <cite>{{ year2025.film.cite[L] }}</cite>
          </blockquote>
        </div>
      </div>
      <div class="home-year-groups">
        <section v-for="g in year2025.groups" :key="g.key" class="home-year-group" :aria-labelledby="'home-year-' + g.key">
          <h3 :id="'home-year-' + g.key" class="home-year-label">{{ g.label[L] }}<span>{{ g.items.length }}</span></h3>
          <component :is="g.image.to ? 'router-link' : 'span'" :to="g.image.to ? localizedPath(g.image.to) : null" class="home-year-pic" :tabindex="g.image.to ? -1 : null" :aria-hidden="g.image.to ? 'true' : null">
            <img loading="lazy" decoding="async" :width="g.image.width" :height="g.image.height" :src="near.film ? g.image.src : null" :alt="g.image.to ? '' : g.image.alt[L]" :style="g.image.position ? { objectPosition: g.image.position } : null" />
          </component>
          <ol class="home-year-list">
            <li v-for="it in g.items" :key="it.date + it.title.en">
              <time>{{ it.date }}</time>
              <router-link v-if="it.to" class="home-year-title" :to="localizedPath(it.to)">{{ it.title[L] }} →</router-link>
              <a v-else-if="it.course" class="home-year-title" v-bind="courseLink(it.course)">{{ it.title[L] }} ↗︎</a>
              <span v-else class="home-year-title">{{ it.title[L] }}</span>
              <span class="home-year-place">{{ it.place[L] }}</span>
              <p>{{ it.line[L] }}</p>
            </li>
          </ol>
        </section>
      </div>
    </section>

    <!-- 04 實驗室：一件在原地跑的作品、他的一句話、數字、一首詩 -->
    <HomeLab :zh="isZh" />

    <!-- 04 其他入口：地層、音樂、思想、關於，各帶一個真的樣本 -->
    <section ref="doorsEl" class="home-sec home-doors" aria-labelledby="home-doors-title">
      <div class="home-head">
        <p class="home-eyebrow">05 · MORE</p>
        <h2 id="home-doors-title">{{ t.doorsTitle }}</h2>
      </div>
      <div class="home-doors-grid">
        <article class="home-door home-door-strata">
          <p class="home-eyebrow">{{ isZh ? '地層' : 'Strata' }} · {{ strataSpan }}</p>
          <h3><router-link :to="localizedPath('/strata')">{{ t.strataTitle }}</router-link></h3>
          <p class="home-door-body">{{ t.strataBody(strataYears.length) }}</p>
          <ul class="home-years">
            <li v-for="y in strataYears" :key="y.year"><router-link :to="localizedPath('/strata/' + y.year)">{{ y.chip }}</router-link></li>
          </ul>
        </article>
        <article class="home-door home-door-music">
          <p class="home-eyebrow">{{ isZh ? '音樂' : 'Music' }} · {{ isZh ? '最新一首' : 'Latest song' }}</p>
          <div class="home-door-row">
            <img v-if="latestSong.cover" loading="lazy" decoding="async" width="96" height="96" :src="near.doors ? latestSong.cover : null" alt="" />
            <div>
              <h3><a :href="latestSong.url" rel="noopener">{{ latestSong.title }} ↗︎</a></h3>
              <p class="home-door-meta">{{ latestSong.publishedOn ? latestSong.publishedOn + ' · ' : '' }}StreetVoice</p>
            </div>
          </div>
          <p class="home-door-body">{{ t.musicBody(allSongs.length, spotifyReleases.length) }}</p>
          <router-link class="home-link" :to="localizedPath('/music')">{{ isZh ? '進入音樂' : 'Go to Music' }} →</router-link>
        </article>
        <article v-if="latestDialogue" class="home-door home-door-thinking">
          <p class="home-eyebrow">{{ isZh ? '思想 · 最新對話錄' : 'Thinking · Latest dialogue' }}</p>
          <h3><router-link :to="localizedPath('/dialogues/' + latestDialogue.slug)">{{ pickLocale(latestDialogue.title) }}</router-link></h3>
          <p class="home-door-meta">No. {{ latestDialogue.no }} · {{ latestDialogue.date }}</p>
          <p class="home-door-body home-clamp">{{ pickLocale(latestDialogue.summary) }}</p>
          <router-link class="home-link" :to="localizedPath('/thinking')">{{ isZh ? '所有思想與文字' : 'All thinking & writing' }} →</router-link>
        </article>
        <article class="home-door home-door-about">
          <p class="home-eyebrow">{{ isZh ? '關於' : 'About' }}</p>
          <div class="home-door-row">
            <img loading="lazy" decoding="async" width="96" height="96" :src="near.doors ? '/static/img/CheYu-0401-crop-sq.jpeg' : null" alt="" />
            <h3><router-link :to="localizedPath('/about')">{{ isZh ? '簡介、履歷與報導' : 'Biography, CV and press' }}</router-link></h3>
          </div>
          <p class="home-door-links">
            <router-link :to="localizedPath('/about')">{{ $t('nav.about') }} →</router-link>
            <router-link :to="localizedPath('/cv')">{{ isZh ? '履歷' : 'CV' }} →</router-link>
            <router-link :to="localizedPath('/press')">{{ isZh ? '報導' : 'Press' }} →</router-link>
            <router-link :to="localizedPath('/contact')">{{ $t('nav.contact') }} →</router-link>
          </p>
        </article>
      </div>
    </section>

    <section class="home-outro">
      <router-link class="home-outro-main" :to="localizedPath('/work/archive')">
        <small>{{ isZh ? '作品、研究與一路留下的紀錄' : 'Works, research, and a continuing record' }}</small>
        <span>{{ isZh ? '進入完整檔案' : 'Enter the archive' }} ↗︎</span>
      </router-link>
      <p class="home-outro-contact">{{ t.contactLine }} <router-link :to="localizedPath('/contact')">{{ $t('nav.contact') }} →</router-link></p>
    </section>

    <dialog ref="filmDialog" class="film-dialog motion-dialog" aria-labelledby="film-title" @close="onFilmClose" @click="closeOnBackdrop">
      <div class="film-dialog-inner">
        <div class="film-heading">
          <h2 id="film-title">{{ hero.filmTitle }}</h2>
          <button type="button" @click="closeFilm"><BrandIcon name="close" />{{ isZh ? '關閉' : 'Close' }}</button>
        </div>
        <iframe v-if="filmOpen && hero.film" :src="'https://www.youtube-nocookie.com/embed/' + hero.film + '?playsinline=1'" :title="hero.filmTitle" allow="encrypted-media; picture-in-picture" allowfullscreen></iframe>
        <p>{{ isZh ? '影片由 YouTube 提供。' : 'Video hosted on YouTube.' }}</p>
      </div>
    </dialog>
  </div>
</template>

<script>
import { withSlash } from '@/runtime/site-path.mjs'
import { isBot } from '@/runtime/view-transition'
import { computed, reactive, ref, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import dialogues from '@/data/dialogues.json'
import works from '@/data/work-catalog.json'
import homeFeatured from '@/data/home-featured.json'
import homeIdentity from '@/data/home-identity.json'
import year2025 from '@/data/home-2025.json'
import { TD_COURSES, courseHref, trackCourseClick } from '@/components/teaching/course-link.mjs'
import { fillCounts } from '@/components/home/home-year.mjs'
import strataIndex from '@/data/lab/index.json'
import { allSongs, spotifyReleases } from '@/data/music-catalog.json'
import { nowStatus, pickHero, slugFromPath, taipeiToday } from '@/components/home/home-now.mjs'
import ClickFilm from '@/components/media/ClickFilm.vue'
import HomeGene from '@/components/home/HomeGene.vue'
import BrandIcon from '@/components/BrandIcon.vue'
import HomeLab from '@/components/home/HomeLab.vue'

const ZH = {
  eyebrow: 'CHE-YU WU · 新媒體藝術 · 聲音 · 演算法',
  toWork: '作品與展覽',
  archive: '完整檔案',
  nowTitle: '近況',
  status: { on: '進行中', soon: '即將', past: '已結束' },
  doorsTitle: '地層、音樂、思想與關於',
  strataTitle: '每一年留下來的東西',
  strataBody: (n) => `從小學到今年，${n} 面牆，每一筆都有公開的來源。`,
  musicBody: (a, b) => `StreetVoice 上 ${a} 首歌，Spotify 發行 ${b} 首鋼琴曲。`,
  contactLine: '展覽、委託與合作邀約：'
}
const EN = {
  eyebrow: 'CHE-YU WU · New media art · Sound · Algorithms',
  toWork: 'Works & Exhibitions',
  archive: 'Full archive',
  nowTitle: 'Now',
  status: { on: 'On now', soon: 'Upcoming', past: 'Ended' },
  doorsTitle: 'Strata, music, thinking and about',
  strataTitle: 'What each year left behind',
  strataBody: (n) => `From primary school to this year: ${n} walls, every record with a public source.`,
  musicBody: (a, b) => `${a} songs on StreetVoice and ${b} piano releases on Spotify.`,
  contactLine: 'Exhibitions, commissions and collaborations:'
}

export default {
  name: 'PageIndex',
  components: { HomeGene, BrandIcon, HomeLab, ClickFilm },
  setup () {
    const { locale, tm } = useI18n()
    const isZh = computed(() => locale.value === 'zh')
    const t = computed(() => (isZh.value ? ZH : EN))
    const localizedPath = path => withSlash(isZh.value ? '/zh' + path : path)
    const pickLocale = value => typeof value === 'string' ? value : value?.[locale.value] || value?.en || value?.zh || ''
    const identity = computed(() => homeIdentity.identity[isZh.value ? 'zh' : 'en'])
    const L = computed(() => (isZh.value ? 'zh' : 'en'))
    // 2025 年度回顧：導言的「幾檔演出、幾項教學」由清單件數填（home-year.mjs），清單改了導言跟著變
    const yearIntro = computed(() => fillCounts(year2025, L.value))
    // 教學區的 TD 項目（2026-10-18）：item.course 是 TD_COURSES 的 key，網址只住在 course-link.mjs；帶 UTM（utm_campaign=teaching-home-teaching）與 course_click
    const courseLink = (key) => {
      const course = TD_COURSES[key]
      return { href: courseHref(course, 'home-teaching', L.value), target: '_blank', rel: 'noopener', onClick: () => trackCourseClick(course, 'home-teaching', L.value) }
    }
    const bySlug = new Map(works.map(w => [w.slug, w]))
    const workTitle = w => (isZh.value ? w.titleZh || w.title : w.title)

    // 近況：狀態（進行中／即將／已結束）只在瀏覽器裡算，預渲染的 HTML 不凍住建置那天的狀態；字的位置先留好，不跳版
    const today = ref('')
    const nowItems = computed(() => {
      const items = tm('about.nowItems')
      if (!Array.isArray(items)) return []
      return items.map(item => {
        const slug = slugFromPath(item.to)
        const work = slug && bySlug.get(slug)
        return { ...item, slug: work ? slug : null, cover: work?.cover || null, status: nowStatus(item.date, today.value) }
      })
    })

    // 主視覺：建置時（預渲染）與瀏覽器（createApp 會整頁重畫）各用當天的台北日期挑一次
    const heroDay = taipeiToday()
    const heroPick = computed(() => {
      const items = tm('about.nowItems')
      const fallback = (homeFeatured.slugs || []).find(slug => bySlug.get(slug)?.cover) || 'the-last-input'
      return pickHero(Array.isArray(items) ? items : [], heroDay, slug => !!bySlug.get(slug)?.cover, fallback)
    })
    const hero = computed(() => {
      const { slug, item } = heroPick.value
      const w = bySlug.get(slug) || {}
      const cfg = homeFeatured.hero?.images?.[slug] || {}
      const L = isZh.value ? 'zh' : 'en'
      return {
        slug, to: item?.to || '/work/' + slug, title: workTitle(w),
        src: cfg.src || w.cover, srcset: cfg.srcset || null, width: cfg.width || 1600, height: cfg.height || 1067,
        position: cfg.position, positionMobile: cfg.positionMobile,
        alt: cfg.alt?.[L] || workTitle(w),
        context: cfg.context?.[L] || (isZh.value ? w.descriptionZh || w.description : w.description),
        credit: cfg.credit?.[L] || '',
        film: cfg.film || null, filmTitle: cfg.filmTitle?.[L] || '',
        eyebrow: item ? `${item.date} · ${item.place}` : `${w.year} · ${categoryLabel(w.category)}`,
        status: item ? nowStatus(item.date, today.value) : null
      }
    })

    // 最新對話錄＝這個語言有內文的對話錄裡，日期最新的那一篇（2026-10-03 哲宇：「首頁的最新對話錄好像沒有在抓最新對話錄」）。
    // 只有中文的對話錄不出現在英文首頁，免得連到不存在的英文頁。
    const latestDialogue = computed(() => {
      const loc = locale.value === 'zh' ? 'zh' : 'en'
      return dialogues.filter(d => (d.available_in || Object.keys(d.sections || {})).includes(loc)).sort((a, b) => String(b.date).localeCompare(String(a.date)) || (b.no || 0) - (a.no || 0))[0] || null
    })
    const featuredWorks = computed(() => {
      // 精選順序住在 src/data/home-featured.json（2026-09-18 起）；還沒進 catalog 的 slug 會被略過，不會出空卡。
      const chosen = (homeFeatured.slugs || []).map(slug => bySlug.get(slug)).filter(Boolean)
      return [...chosen, ...works.filter(work => !chosen.includes(work) && !work.slug.includes('last-input'))].slice(0, homeFeatured.limit || 8)
    })
    const categoryLabel = category => {
      const labels = { installation: ['裝置', 'Installation'], generative: ['生成藝術', 'Generative art'], nft: ['生成藝術', 'Generative art'], opensource: ['開源計畫', 'Open source'], performance: ['演出', 'Performance'] }
      return labels[category]?.[isZh.value ? 0 : 1] || category
    }

    // 地層：年份膠囊，新到舊；求學時期的牆用標籤第一個詞（大學／國高中／小學）
    const strataYears = computed(() => [...strataIndex].sort((a, b) => b.period.end.localeCompare(a.period.end)).map(e => {
      const label = e.label?.[isZh.value ? 'zh' : 'en'] || e.year
      return { year: e.year, chip: /^\d{4}/.test(label) ? e.year : label.replace(/\s*\d{4}.*$/, '') }
    }))
    const strataSpan = `${Math.min(...strataIndex.map(e => Number(e.period.start.slice(0, 4))))}–${Math.max(...strataIndex.map(e => Number(e.period.end.slice(0, 4))))}`
    const latestSong = [...allSongs].sort((a, b) => String(b.publishedOn || '').localeCompare(String(a.publishedOn || '')))[0] || {}

    // 第一屏以下的圖：區塊捲到視窗前 600px 才給 src；預渲染的靜態 HTML 完全不給（同 /lab/ 的牆）。
    // 靜態 HTML 裡的 lazy 圖，手機瀏覽器在 JS 接手前就照樣下載（2026-10-04 預覽站量到：近況、精選、實驗室共 10 張一進頁就載，發起者是 parser）。
    const nowEl = ref(null), worksEl = ref(null), filmEl = ref(null), doorsEl = ref(null)
    const near = reactive({ now: false, works: false, film: false, doors: false })
    let nearIo = null
    // 主視覺的基因條用主視覺實際載入的那張圖（srcset 挑中的 currentSrc；同源就取路徑，對得上 gene-strips.json 的鍵），不另外下載
    const heroImg = ref(null), heroShown = ref('')
    let mounted = false, prerendering = false
    const onHeroLoad = () => {
      const img = heroImg.value
      if (!mounted || prerendering || !img?.currentSrc || !img.naturalWidth) return
      try { const u = new URL(img.currentSrc, location.href); heroShown.value = u.origin === location.origin ? decodeURI(u.pathname) : u.href } catch (e) { heroShown.value = img.currentSrc }
    }
    onMounted(() => {
      const prerender = isBot() && typeof navigator !== 'undefined' && navigator.webdriver === true
      if (!prerender) {
        const els = [['now', nowEl.value], ['works', worksEl.value], ['film', filmEl.value], ['doors', doorsEl.value]].filter(([, el]) => el)
        if (typeof IntersectionObserver !== 'function') for (const [k] of els) near[k] = true
        else {
          nearIo = new IntersectionObserver(entries => { for (const e of entries) if (e.isIntersecting) { near[e.target.dataset.near] = true; nearIo.unobserve(e.target) } }, { rootMargin: '600px 0px' })
          for (const [k, el] of els) { el.dataset.near = k; nearIo.observe(el) }
        }
      }
      today.value = taipeiToday()
      // 預渲染（無頭瀏覽器＝isBot 且 navigator.webdriver，同 /lab/ 的判斷）不放主視覺的基因條
      prerendering = prerender
      mounted = true
      if (heroImg.value?.complete) onHeroLoad()
    })

    const filmDialog = ref(null)
    const filmTrigger = ref(null)
    const filmOpen = ref(false)
    const openFilm = () => { filmOpen.value = true; filmDialog.value?.showModal() }
    const closeFilm = () => filmDialog.value?.close()
    const onFilmClose = () => { filmOpen.value = false; filmTrigger.value?.focus() }
    const closeOnBackdrop = event => { if (event.target === filmDialog.value) closeFilm() }
    onBeforeUnmount(() => { filmDialog.value?.close(); nearIo?.disconnect() })

    return { isZh, t, near, nowEl, worksEl, filmEl, doorsEl, hero, localizedPath, pickLocale, identity, workTitle, latestDialogue, nowItems, featuredWorks, categoryLabel, strataYears, strataSpan, latestSong, allSongs, spotifyReleases, year2025, yearIntro, courseLink, L, heroImg, heroShown, onHeroLoad, filmDialog, filmTrigger, filmOpen, openFilm, closeFilm, onFilmClose, closeOnBackdrop }
  }
}
</script>

<style lang="sass">
.home
  color: var(--ink)
  text-align: left
  --home-gap: clamp(72px, 9vw, 144px)
  .home-top, .home-hero-cap, .home-sec, .home-outro
    width: var(--editorial-w)
    margin-left: auto
    margin-right: auto
  .home-eyebrow
    display: block
    font-family: var(--font-body)
    font-size: 0.74rem
    font-weight: 500
    letter-spacing: 0.14em
    line-height: 1.7
    text-transform: uppercase
    color: var(--ink-3)
    margin: 0
  .home-link
    display: inline-block
    color: var(--ink)
    font-size: 0.88rem
    padding: 6px 0
    text-decoration: none
    white-space: nowrap
    transition: var(--t-hover)
    &:hover
      color: var(--gold)
  .home-pill
    display: inline-flex
    align-items: center
    gap: 6px
    padding: 10px 18px
    border-radius: var(--r-pill)
    background: var(--tint-2)
    color: var(--ink)
    font-size: 0.88rem
    text-decoration: none
    transition: var(--t-hover)
    &:hover
      background: var(--tint-3)
      color: var(--ink)
    &.is-primary
      background: #c5af7f
      color: #000
      &:hover
        background: var(--gold)
        color: #000
  .home-actions
    display: flex
    flex-wrap: wrap
    gap: 10px
    margin: 0

  // ── 頁首：他是誰 ──
  .home-top
    display: grid
    grid-template-columns: minmax(0, 7fr) minmax(0, 5fr)
    gap: 24px clamp(32px, 5vw, 96px)
    align-items: end
    padding: clamp(40px, 5vw, 80px) 0 clamp(28px, 3vw, 44px)
  .hero-name
    display: flex
    align-items: baseline
    flex-wrap: wrap
    gap: 0.06em 0.3em
    font-family: var(--font-slab)
    font-size: clamp(2.8rem, 7.6vw, 8.2rem)
    font-weight: 400
    line-height: 1
    letter-spacing: -0.03em
    margin: 14px 0 18px
    span
      white-space: nowrap
    .name-chinese
      font-family: var(--font-display)
      font-size: 0.46em
      letter-spacing: 0.12em
  .home-motto
    font-family: var(--font-display)
    font-size: clamp(1.05rem, 1.5vw, 1.3rem)
    color: var(--ink-2)
    margin: 0
  .home-identity
    font-size: clamp(1rem, 1.25vw, 1.14rem)
    line-height: 1.9
    color: var(--ink-2)
    margin: 0 0 20px
    max-width: 40em

  // ── 主視覺 ──
  .home-hero
    margin: 0
  .home-hero-frame
    position: relative
    background: #141310
  .home-hero-image
    display: block
    width: 100%
    height: clamp(360px, 52vw, 820px)
    object-fit: cover
    object-position: var(--pos, center)
  .home-film
    position: absolute
    right: 3vw
    bottom: 24px
    display: inline-flex
    align-items: center
    gap: 8px
    padding: 10px 18px
    border: 0
    border-radius: var(--r-pill)
    background: rgba(0, 0, 0, 0.62)
    backdrop-filter: blur(8px)
    color: var(--ink)
    font-size: 0.86rem
    cursor: pointer
    transition: var(--t-hover)
    svg
      width: 15px
      height: 15px
    &:hover
      background: rgba(0, 0, 0, 0.82)
      color: var(--gold)
  // 基因條（2026-10-04 19:00 哲宇：貼著照片像壞掉）：粗細與間距照 /work 卡片「作品名下方的條」（16px、上 18px），屬於文字資訊區
  .home-hero-gene
    display: block
    max-width: 600px
    height: 16px
    margin: 18px 0 8px
    --gene-h: 16px
  .home-hero-cap
    display: grid
    grid-template-columns: 1fr 1fr
    gap: 48px
    padding: 28px 0 0
  .home-hero-work
    color: var(--ink)
    text-decoration: none
    h2
      font-family: var(--font-display)
      font-size: clamp(1.7rem, 2.8vw, 2.8rem)
      font-weight: 400
      line-height: 1.25
      margin: 6px 0 4px
    &:hover .home-link
      color: var(--gold)
  .home-hero-context
    align-self: center
    justify-self: end
    max-width: 46ch
    p
      color: var(--ink-2)
      line-height: 1.8
      margin: 0 0 6px
    small
      color: var(--ink-3)
      font-size: 0.72rem

  // ── 章節共用 ──
  .home-sec
    padding-top: var(--home-gap)
  .home-sec + .home-sec
    margin-top: clamp(56px, 6vw, 96px)
    border-top: 1px solid var(--line)
  .home-head
    margin-bottom: clamp(28px, 3vw, 44px)
    h2
      font-family: var(--font-display)
      font-weight: 400
      font-size: clamp(2rem, 3.6vw, 3.4rem)
      line-height: 1.2
      margin: 10px 0 0
  .home-head-row
    display: flex
    justify-content: space-between
    align-items: flex-end
    gap: 20px
  .home-head-links
    display: flex
    gap: 24px
    margin: 0

  // ── 01 近況 ──
  .home-now
    padding-top: clamp(56px, 6vw, 96px)
  .home-now-list
    display: grid
    grid-template-columns: repeat(4, minmax(0, 1fr))
    gap: 28px 20px
  .home-now-item
    display: flex
    flex-direction: column
    color: var(--ink)
    text-decoration: none
    &:hover
      color: var(--ink)
      .home-now-label
        color: var(--gold)
      img
        transform: scale(1.025)
  .home-now-pic
    display: block
    overflow: hidden
    aspect-ratio: 3 / 2
    background: var(--bg1)
    img
      display: block
      width: 100%
      height: 100%
      object-fit: cover
      transition: transform var(--dur-scene) var(--ease-out)
  .home-now-gene
    display: block
    margin-top: auto
    padding-top: 16px
    --gene-h: 14px
  .home-now-when
    display: flex
    align-items: baseline
    justify-content: space-between
    gap: 10px
    margin-top: 14px
    time
      font-family: var(--font-slab)
      font-size: 1.3rem
      font-variant-numeric: tabular-nums
  .home-now-status
    min-height: 1.4em
    font-style: normal
    font-size: 0.74rem
    color: var(--ink-3)
    white-space: nowrap
    &.is-on, &.is-soon
      color: var(--gold)
    &.is-on::before, &.is-soon::before
      content: ''
      display: inline-block
      width: 6px
      height: 6px
      margin-right: 6px
      border-radius: 50%
      background: currentColor
      vertical-align: 1px
  .home-now-label
    margin-top: 6px
    font-size: 0.94rem
    line-height: 1.6
    transition: var(--t-hover)
  .home-now-place
    margin-top: 4px
    font-size: 0.78rem
    color: var(--ink-3)

  // ── 02 精選作品 ──
  .home-works
    content-visibility: auto
    contain-intrinsic-size: auto 2000px
  .home-works-grid
    list-style: none
    margin: 0
    padding: 0
    display: grid
    grid-template-columns: repeat(12, minmax(0, 1fr))
    gap: 56px 24px
    > li
      grid-column: span 4
      &.is-large
        grid-column: span 6
  .home-work
    display: block
    color: var(--ink)
    text-decoration: none
    &:hover
      color: var(--ink)
      h3
        color: var(--gold)
      img
        transform: scale(1.025)
    .selected-image
      display: block
      overflow: hidden
      aspect-ratio: 3 / 2
      background: var(--bg1)
      margin-bottom: 16px
      img
        display: block
        width: 100%
        height: 100%
        object-fit: cover
        transition: transform var(--dur-scene) var(--ease-out)
    h3
      font-family: var(--font-display)
      font-weight: 400
      font-size: clamp(1.2rem, 1.6vw, 1.55rem)
      line-height: 1.35
      margin: 4px 0 0
      transition: var(--t-hover)
    p
      color: var(--ink-2)
      font-size: 0.94rem
      line-height: 1.75
      max-width: 56ch
      margin: 14px 0 0
  .is-large .home-work h3
    font-size: clamp(1.5rem, 2.2vw, 2.1rem)
  .home-work-gene
    --gene-h: 14px
    margin-top: 14px
  .is-large .home-work-gene
    --gene-h: 16px
    margin-top: 18px

  // ── 03 · 2025 年度回顧：導言＋影片，下面四組（演出、展出、教學、合作），組標＋代表圖＋每件一行 ──
  .home-year
    content-visibility: auto
    contain-intrinsic-size: auto 1800px
  .home-year-top
    display: grid
    grid-template-columns: minmax(0, 5fr) minmax(0, 7fr)
    gap: 32px clamp(32px, 4vw, 72px)
    align-items: start
  .home-year-intro
    font-size: clamp(1rem, 1.15vw, 1.1rem)
    line-height: 1.95
    color: var(--ink)
    margin: 0
    max-width: 34em
  .home-year-quote
    margin: 18px 0 0
    padding: 0
    p
      font-family: var(--font-display)
      font-size: 1rem
      line-height: 1.8
      color: var(--ink-2)
      margin: 0
    cite
      display: block
      margin-top: 4px
      font-size: 0.74rem
      font-style: normal
      color: var(--ink-3)
  .home-year-groups
    display: grid
    grid-template-columns: repeat(4, minmax(0, 1fr))
    gap: 48px 24px
    margin-top: clamp(56px, 6vw, 96px)
  .home-year-label
    display: flex
    align-items: baseline
    gap: 10px
    font-family: var(--font-display)
    font-weight: 400
    font-size: clamp(1.35rem, 1.8vw, 1.7rem)
    line-height: 1.3
    margin: 0 0 16px
    span
      font-family: var(--font-slab)
      font-size: 0.7em
      color: var(--gold)
      font-variant-numeric: tabular-nums
  .home-year-pic
    display: block
    overflow: hidden
    aspect-ratio: 3 / 2
    background: var(--bg1)
    margin-bottom: 6px
    img
      display: block
      width: 100%
      height: 100%
      object-fit: cover
      transition: transform var(--dur-scene) var(--ease-out)
  a.home-year-pic:hover img
    transform: scale(1.025)
  .home-year-list
    list-style: none
    margin: 0
    padding: 0
    li
      display: flex
      flex-direction: column
      padding: 14px 0 16px
      border-bottom: 1px solid var(--line)
    time
      font-family: var(--font-slab)
      font-size: 0.98rem
      font-variant-numeric: tabular-nums
      color: var(--ink-2)
    p
      font-size: 0.86rem
      line-height: 1.75
      color: var(--ink-2)
      margin: 6px 0 0
  .home-year-title
    margin-top: 4px
    font-size: 1rem
    line-height: 1.55
    color: var(--ink)
  a.home-year-title
    text-decoration: none
    transition: var(--t-hover)
    &:hover
      color: var(--gold)
  .home-year-place
    margin-top: 2px
    font-size: 0.78rem
    color: var(--ink-3)
  .home-hero-work .home-now-status
    margin-left: 12px

  // ── 04 實驗室（元件在 components/home/HomeLab.vue）──
  .home-lab
    content-visibility: auto
    contain-intrinsic-size: auto 1100px

  // ── 05 入口 ──
  .home-doors
    content-visibility: auto
    contain-intrinsic-size: auto 900px
  .home-doors-grid
    display: grid
    grid-template-columns: repeat(2, minmax(0, 1fr))
    gap: 20px
  .home-door
    display: flex
    flex-direction: column
    gap: 14px
    padding: clamp(22px, 2.4vw, 36px)
    background: var(--bg1)
    border-radius: var(--r-lg)
    transition: background-color var(--dur-quick) var(--ease-out)
    &:hover
      background: var(--bg2)
    h3
      font-family: var(--font-display)
      font-weight: 400
      font-size: clamp(1.35rem, 2vw, 1.9rem)
      line-height: 1.35
      margin: 0
      a
        color: var(--ink)
        text-decoration: none
        &:hover
          color: var(--gold)
    > .home-link
      margin-top: auto
      align-self: flex-start
  .home-door-body
    color: var(--ink-2)
    line-height: 1.8
    margin: 0
  .home-door-meta
    color: var(--ink-3)
    font-size: 0.8rem
    margin: 4px 0 0
  .home-clamp
    display: -webkit-box
    -webkit-line-clamp: 3
    -webkit-box-orient: vertical
    overflow: hidden
  .home-door-row
    display: flex
    align-items: center
    gap: 18px
    img
      flex: none
      width: 96px
      height: 96px
      object-fit: cover
      display: block
  .home-years
    list-style: none
    margin: auto 0 0
    padding: 0
    display: flex
    flex-wrap: wrap
    gap: 8px
    a
      display: inline-block
      padding: 6px 13px
      border-radius: var(--r-pill)
      background: var(--tint-2)
      color: var(--ink-2)
      font-size: 0.8rem
      font-variant-numeric: tabular-nums
      text-decoration: none
      transition: var(--t-hover)
      &:hover
        background: var(--tint-3)
        color: var(--ink)
  .home-door-links
    display: flex
    flex-wrap: wrap
    gap: 8px 22px
    margin: auto 0 0
    a
      color: var(--ink)
      font-size: 0.88rem
      text-decoration: none
      &:hover
        color: var(--gold)

  // ── 出口 ──
  .home-outro
    content-visibility: auto
    contain-intrinsic-size: auto 320px
    margin-top: var(--home-gap)
    padding: 40px 0 24px
    border-top: 1px solid var(--line)
  .home-outro-main
    display: flex
    flex-direction: column
    gap: 8px
    color: var(--ink)
    text-decoration: none
    small
      font-size: 0.74rem
      letter-spacing: 0.14em
      text-transform: uppercase
      color: var(--ink-3)
    span
      font-family: var(--font-display)
      font-size: clamp(2rem, 5vw, 4.6rem)
      line-height: 1.2
      transition: var(--t-hover)
    &:hover span
      color: var(--gold)
  .home-outro-contact
    color: var(--ink-2)
    margin: 18px 0 0
    a
      color: var(--ink)
      &:hover
        color: var(--gold)

  // ── 影片對話框 ──
  .film-dialog
    position: fixed
    inset: 0
    padding: 0
    border: 0
    border-radius: var(--r-md)
    width: min(1100px, 94vw)
    max-width: 94vw
    max-height: 92dvh
    margin: auto
    color: var(--ink)
    background: var(--bg1)
    &::backdrop
      background: rgba(0, 0, 0, 0.9)
    .film-dialog-inner
      padding: 20px
    .film-heading
      display: flex
      justify-content: space-between
      align-items: center
      gap: 16px
      margin-bottom: 16px
      h2
        font-size: 1rem
        margin: 0
      button
        display: inline-flex
        align-items: center
        gap: 6px
        flex-shrink: 0
        min-height: 44px
        color: var(--ink)
        background: var(--tint-2)
        border: 0
        border-radius: var(--r-pill)
        padding: 8px 16px
        cursor: pointer
        svg
          width: 16px
          height: 16px
    iframe
      width: 100%
      aspect-ratio: 16 / 9
      border: 0
      display: block
    p
      margin: 12px 0 0
      font-size: 0.8rem
      color: var(--ink-2)

  // ── 平板與手機 ──
  @media (max-width: 1100px)
    .home-now-list
      grid-template-columns: repeat(2, minmax(0, 1fr))
    .home-year-groups
      grid-template-columns: repeat(2, minmax(0, 1fr))
  @media (max-width: 860px)
    .home-top, .home-hero-cap
      grid-template-columns: 1fr
    .home-hero-context
      justify-self: start
    .home-hero-cap
      gap: 16px
    .home-doors-grid
      grid-template-columns: 1fr
    .home-year-top
      grid-template-columns: 1fr
    .home-works-grid
      grid-template-columns: repeat(2, minmax(0, 1fr))
      gap: 36px 14px
      > li, > li.is-large
        grid-column: span 1
      > li.is-large
        grid-column: 1 / -1
    .home-works-grid > li:not(.is-large) .home-work
      h3
        font-size: 1.02rem
      .home-eyebrow
        font-size: 0.66rem
  @media (max-width: 640px)
    .hero-name
      font-size: clamp(2.6rem, 12vw, 4rem)
      gap: 6px 16px
      .name-chinese
        font-size: 0.56em
    .home-hero-image
      height: 420px
      object-position: var(--pos-m, center)
    // 手機的條短一點：主視覺手機只載 1000 寬那張，條寬 × DPR 不超過緊裁剩下的像素寬，才裁得掉暗段又不糊（GeneStrip 的放寬規則）
    .home-hero-gene
      max-width: 62%
    .home-film
      right: 16px
      bottom: 16px
      padding: 8px 14px
    .home-head-row
      flex-direction: column
      align-items: flex-start
    .home-now-list
      grid-template-columns: 1fr
      gap: 0
    .home-year-groups
      grid-template-columns: 1fr
      gap: 44px
    .home-year-pic
      aspect-ratio: 2 / 1
    .home-now-item
      display: grid
      grid-template-columns: 112px minmax(0, 1fr)
      grid-template-rows: auto auto auto
      column-gap: 16px
      padding: 16px 0
      border-top: 1px solid var(--line)
      .home-now-pic
        grid-row: 1 / span 3
        align-self: start
      .home-now-gene
        display: none
      .home-now-when
        margin-top: 0
        time
          font-size: 1.05rem
      .home-now-label
        margin-top: 2px
        font-size: 0.9rem
// 開場的基因條拉成全寬線之後退場；首頁主視覺下方（與近況卡）的基因條在圖到的那一刻由左往右畫出來，接住同一個動作。減少動態不畫。
@media (prefers-reduced-motion: no-preference)
  .home-hero-gene .gene-strip.is-loaded, .home-now-gene .gene-strip.is-loaded
    animation: home-gene-draw 1.4s cubic-bezier(.65, 0, .35, 1) both
  .home-now-gene .gene-strip.is-loaded
    animation-duration: 1s
@keyframes home-gene-draw
  from
    clip-path: inset(0 100% 0 0)
  to
    clip-path: inset(0 0 0 0)
</style>
